import { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Mic, MicOff, Package, Check, AlertCircle, Camera, X, Loader2, ImagePlus, Edit3, MapPin, RotateCcw, RotateCw } from 'lucide-react';
import { api } from '../services/api';
import { useAuthStore } from '../store/auth';
import { useVoiceSession } from '../hooks/useVoiceSession';
import { useAiCredits } from '../hooks/useAiCredits';
import { PaywallModal } from '../components/common/PaywallModal';
import { PhraseDropdown } from '../components/common/PhraseDropdown';
import { ITEM_CONDITIONS, ITEM_CONDITION_LABELS, type ItemCondition } from '@findmystuff/shared';
import clsx from 'clsx';

type InputMode = 'manual' | 'voice' | 'image';

interface CapturedImage {
  data: string;
  mimeType: string;
  preview: string;
}


export function AddInventoryPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);

  const {
    sessionState,
    extractedItems,
    audioLevel,
    error,
    startSession,
    endSession,
  } = useVoiceSession();

  // AI Credits
  const { hasCredits, consumeCredit } = useAiCredits();
  const [showPaywall, setShowPaywall] = useState(false);
  const [paywallFeature, setPaywallFeature] = useState('');

  const { data: inventories } = useQuery({
    queryKey: ['inventories'],
    queryFn: () => api.inventories.list(),
  });

  // Get inventoryId from URL params, fallback to user default
  const urlInventoryId = searchParams.get('inventoryId');
  const [selectedInventory, setSelectedInventory] = useState(
    urlInventoryId || user?.settings?.defaultInventoryId || ''
  );

  // Track if we've initialized the inventory selection
  const [inventoryInitialized, setInventoryInitialized] = useState(false);

  // Only set inventory from URL on initial load, not on every render
  useEffect(() => {
    if (!inventoryInitialized && inventories && inventories.length > 0) {
      // First time: use URL param if valid, otherwise use first inventory
      if (urlInventoryId && inventories.some(inv => inv.id === urlInventoryId)) {
        setSelectedInventory(urlInventoryId);
      } else if (!selectedInventory) {
        setSelectedInventory(inventories[0]!.id);
      }
      setInventoryInitialized(true);
    }
  }, [inventories, inventoryInitialized, urlInventoryId, selectedInventory]);

  // Fetch locations from API
  const { data: savedLocations = [] } = useQuery({
    queryKey: ['locations'],
    queryFn: api.locations.list,
  });

  // Auto-select the most recent location when locations load
  const [locationInitialized, setLocationInitialized] = useState(false);
  useEffect(() => {
    if (!locationInitialized && savedLocations.length > 0) {
      const lastLocation = savedLocations[0]; // sorted by updatedAt desc
      if (lastLocation) {
        setSelectedLocationId(lastLocation.id);
        setManualLocation(lastLocation.name);
        setLocationMode('select');
      }
      setLocationInitialized(true);
    } else if (!locationInitialized && savedLocations.length === 0) {
      setLocationMode('new');
      setLocationInitialized(true);
    }
  }, [savedLocations, locationInitialized]);
  const [localError, setLocalError] = useState('');
  const [inputMode, setInputMode] = useState<InputMode>('manual');
  const [capturedImages, setCapturedImages] = useState<CapturedImage[]>([]);
  const [imageItems, setImageItems] = useState<Array<{ id: string; title: string; category: string; confidence: number }>>([]);
  const [imageSessionState, setImageSessionState] = useState<'idle' | 'analyzing' | 'done'>('idle');

  // Manual mode state
  const [manualItemName, setManualItemName] = useState('');
  const [manualDescription, setManualDescription] = useState('');
  const [manualLocation, setManualLocation] = useState('');
  const [manualLocationImage, setManualLocationImage] = useState<CapturedImage | null>(null);
  const [manualCondition, setManualCondition] = useState<ItemCondition>('good');
  const [manualImages, setManualImages] = useState<CapturedImage[]>([]);
  const [manualSessionState, setManualSessionState] = useState<'idle' | 'saving' | 'done'>('idle');
  const [manualCreatedItem, setManualCreatedItem] = useState<{ id: string; title: string } | null>(null);

  // Location selection state
  const [locationMode, setLocationMode] = useState<'select' | 'new'>('select');
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const manualFileInputRef = useRef<HTMLInputElement>(null);
  const manualCameraInputRef = useRef<HTMLInputElement>(null);
  const locationImageInputRef = useRef<HTMLInputElement>(null);
  const addItemButtonRef = useRef<HTMLButtonElement>(null);

  const analyzeImagesMutation = useMutation({
    mutationFn: (images: Array<{ data: string; mimeType: string }>) =>
      api.realtime.analyzeImages(selectedInventory, images),
    onSuccess: (data) => {
      setImageItems(data.items);
      setImageSessionState('done');
      // Refresh inventories and items after adding
      queryClient.invalidateQueries({ queryKey: ['inventories'] });
      queryClient.invalidateQueries({ queryKey: ['items'] });
    },
    onError: (err) => {
      setLocalError(err instanceof Error ? err.message : 'Failed to analyze images');
      setImageSessionState('idle');
    },
  });

  const createLocationMutation = useMutation({
    mutationFn: ({ name, imageData }: { name: string; imageData?: { data: string; mimeType: string } }) =>
      api.locations.create(name, imageData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['locations'] });
    },
  });

  const createManualItemMutation = useMutation({
    mutationFn: (data: { inventoryId: string; name: string; description?: string; location?: string; locationImage?: { data: string; mimeType: string }; condition?: ItemCondition; images: Array<{ data: string; mimeType: string }> }) =>
      api.items.createManual(data.inventoryId, data.name, data.description, data.location, data.locationImage, data.condition, data.images),
    onSuccess: async (data) => {
      setManualCreatedItem({ id: data.id, title: data.title });
      setManualSessionState('done');

      // Save location to API if we have one
      const locationName = manualLocation.trim();
      if (locationName) {
        try {
          await createLocationMutation.mutateAsync({
            name: locationName,
            imageData: manualLocationImage ? {
              data: manualLocationImage.data,
              mimeType: manualLocationImage.mimeType,
            } : undefined,
          });
        } catch (error) {
          // Location might already exist, that's ok - upsert handles it
          console.log('Location save:', error);
        }
      }

      // Refresh inventories and items after adding
      queryClient.invalidateQueries({ queryKey: ['inventories'] });
      queryClient.invalidateQueries({ queryKey: ['items'] });
    },
    onError: (err) => {
      setLocalError(err instanceof Error ? err.message : 'Failed to create item');
      setManualSessionState('idle');
    },
  });

  const handleStartSession = async () => {
    if (!selectedInventory) {
      setLocalError('Please select an inventory');
      return;
    }

    // Check AI credits
    if (!hasCredits) {
      setPaywallFeature('Voice Item Extraction');
      setShowPaywall(true);
      return;
    }

    // Consume credit
    const { success } = await consumeCredit();
    if (!success) {
      setPaywallFeature('Voice Item Extraction');
      setShowPaywall(true);
      return;
    }

    setLocalError('');
    await startSession(selectedInventory);
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    // Process files sequentially to maintain order
    const newImages: CapturedImage[] = [];
    for (const file of Array.from(files)) {
      if (!file.type.startsWith('image/')) continue;

      const result = await new Promise<string | null>((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(file);
      });

      if (result) {
        const base64 = result.split(',')[1];
        if (base64) {
          newImages.push({
            data: base64,
            mimeType: file.type,
            preview: result,
          });
        }
      }
    }

    // Add all images at once to maintain order
    if (newImages.length > 0) {
      setCapturedImages((prev) => [...prev, ...newImages]);
    }

    // Reset input
    e.target.value = '';
  };

  const removeImage = (index: number) => {
    setCapturedImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAnalyzeImages = async () => {
    if (!selectedInventory) {
      setLocalError('Please select an inventory');
      return;
    }
    if (capturedImages.length === 0) {
      setLocalError('Please add at least one image');
      return;
    }

    // Check AI credits
    if (!hasCredits) {
      setPaywallFeature('Image Analysis');
      setShowPaywall(true);
      return;
    }

    // Consume credit
    const { success } = await consumeCredit();
    if (!success) {
      setPaywallFeature('Image Analysis');
      setShowPaywall(true);
      return;
    }

    setLocalError('');
    setImageSessionState('analyzing');
    analyzeImagesMutation.mutate(
      capturedImages.map((img) => ({ data: img.data, mimeType: img.mimeType }))
    );
  };

  const handleNewImageSession = () => {
    setCapturedImages([]);
    setImageItems([]);
    setImageSessionState('idle');
    setLocalError('');
  };

  // Manual mode handlers
  const handleManualFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    // Process files sequentially to maintain order
    const newImages: CapturedImage[] = [];
    for (const file of Array.from(files)) {
      if (!file.type.startsWith('image/')) continue;

      const result = await new Promise<string | null>((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(file);
      });

      if (result) {
        const base64 = result.split(',')[1];
        if (base64) {
          newImages.push({
            data: base64,
            mimeType: file.type,
            preview: result,
          });
        }
      }
    }

    // Add all images at once to maintain order
    if (newImages.length > 0) {
      setManualImages((prev) => [...prev, ...newImages]);
      // Scroll to Add Item button after images are added
      setTimeout(() => {
        addItemButtonRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    }

    e.target.value = '';
  };

  const removeManualImage = (index: number) => {
    setManualImages((prev) => prev.filter((_, i) => i !== index));
  };

  const rotateManualImage = async (index: number, direction: 'left' | 'right') => {
    const img = manualImages[index];
    if (!img) return;

    // Create an image element to load the current preview
    const image = new Image();
    image.src = img.preview;

    await new Promise<void>((resolve) => {
      image.onload = () => resolve();
    });

    // Create canvas and rotate
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Swap dimensions for 90-degree rotation
    canvas.width = image.height;
    canvas.height = image.width;

    // Rotate
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate(direction === 'right' ? Math.PI / 2 : -Math.PI / 2);
    ctx.drawImage(image, -image.width / 2, -image.height / 2);

    // Get the rotated image as base64
    const rotatedDataUrl = canvas.toDataURL(img.mimeType);
    const rotatedBase64 = rotatedDataUrl.split(',')[1];

    if (rotatedBase64) {
      setManualImages((prev) =>
        prev.map((item, i) =>
          i === index
            ? { ...item, data: rotatedBase64, preview: rotatedDataUrl }
            : item
        )
      );
    }
  };

  const handleLocationImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;

    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const base64 = result.split(',')[1];
      if (base64) {
        setManualLocationImage({
          data: base64,
          mimeType: file.type,
          preview: result,
        });
      }
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleSelectSavedLocation = (locationId: string) => {
    const location = savedLocations.find(loc => loc.id === locationId);
    if (location) {
      setSelectedLocationId(locationId);
      setManualLocation(location.name);
      // Clear any new location image - existing locations have imageUrl stored in DB
      setManualLocationImage(null);
      setLocationMode('select');
    }
  };

  const handleCreateNewLocation = () => {
    setSelectedLocationId(null);
    setManualLocation('');
    setManualLocationImage(null);
    setLocationMode('new');
  };

  const generateDefaultItemName = () => {
    const now = new Date();
    const formatted = now.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    return `New item - ${formatted}`;
  };

  const handleCreateManualItem = async () => {
    if (!selectedInventory) {
      setLocalError('Please select an inventory');
      return;
    }
    if (manualImages.length === 0) {
      setLocalError('Please add at least one photo');
      return;
    }

    setLocalError('');
    setManualSessionState('saving');

    const itemName = manualItemName.trim() || generateDefaultItemName();
    const itemDescription = manualDescription.trim() || undefined;
    const itemLocation = manualLocation.trim() || undefined;

    createManualItemMutation.mutate({
      inventoryId: selectedInventory,
      name: itemName,
      description: itemDescription,
      location: itemLocation,
      locationImage: manualLocationImage ? { data: manualLocationImage.data, mimeType: manualLocationImage.mimeType } : undefined,
      condition: manualCondition,
      images: manualImages.map((img) => ({ data: img.data, mimeType: img.mimeType })),
    });
  };

  const handleNewManualItem = () => {
    setManualItemName('');
    setManualDescription('');
    // Keep the location selected for convenience when adding multiple items
    // to the same location. The user can change it if needed.
    setManualCondition('good');
    setManualImages([]);
    setManualCreatedItem(null);
    setManualSessionState('idle');
    setLocalError('');
  };

  const handleNewSession = () => {
    window.location.reload();
  };

  const finishAndReview = () => {
    navigate(`/inventory/${selectedInventory}`);
  };

  const displayError = error || localError;
  const isVoiceIdle = sessionState === 'idle' || sessionState === 'error';
  const isImageIdle = imageSessionState === 'idle';
  const isManualIdle = manualSessionState === 'idle';
  const showModeSelector = isVoiceIdle && isImageIdle && isManualIdle;

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-2xl font-bold text-gray-900">Add Inventory</h1>
        <p className="mt-2 text-gray-600">
          {inputMode === 'manual'
            ? 'Add a single item with photos. Name it or let us timestamp it for you.'
            : inputMode === 'voice'
            ? 'Walk around and describe your items naturally. The AI will catalog everything.'
            : 'Take photos of your items and AI will identify them automatically.'}
        </p>
      </div>

      {/* Mode selector - only show when idle */}
      {showModeSelector && (
        <div className="flex gap-2 p-1 bg-gray-100 rounded-lg">
          <button
            onClick={() => setInputMode('manual')}
            className={clsx(
              'flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-2',
              inputMode === 'manual'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            )}
          >
            <Edit3 className="w-4 h-4" />
            Manual
          </button>
          <button
            disabled
            className="flex-1 py-2 px-4 rounded-md text-sm font-medium flex items-center justify-center gap-2 text-gray-400 cursor-not-allowed opacity-50"
          >
            <Mic className="w-4 h-4" />
            Voice
          </button>
          <button
            disabled
            className="flex-1 py-2 px-4 rounded-md text-sm font-medium flex items-center justify-center gap-2 text-gray-400 cursor-not-allowed opacity-50"
          >
            <Camera className="w-4 h-4" />
            Camera
          </button>
        </div>
      )}

      {/* ==================== MANUAL MODE ==================== */}
      {inputMode === 'manual' && (
        <>
          {manualSessionState === 'idle' && (
            <div className="card p-6 space-y-6">
              {/* Inventory selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Add item to:
                </label>
                <select
                  value={selectedInventory}
                  onChange={(e) => setSelectedInventory(e.target.value)}
                  className="input"
                >
                  <option value="">Select an inventory</option>
                  {inventories?.map((inv) => (
                    <option key={inv.id} value={inv.id}>
                      {inv.name} ({inv.itemCount} items)
                    </option>
                  ))}
                </select>
              </div>

              {/* Item name input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Item name (optional):
                </label>
                <input
                  type="text"
                  value={manualItemName}
                  onChange={(e) => setManualItemName(e.target.value)}
                  placeholder="Leave empty for auto-generated name"
                  className="input"
                  spellCheck={true}
                  autoCorrect="on"
                  autoCapitalize="sentences"
                  autoComplete="one-time-code"
                />
                <p className="mt-1 text-xs text-gray-500">
                  If left empty, will be named "New item - {new Date().toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}..."
                </p>
              </div>

              {/* Description input */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Description (optional):
                  </label>
                  <PhraseDropdown
                    onInsert={(phrase) => {
                      const currentDesc = manualDescription;
                      const separator = currentDesc && !currentDesc.endsWith('\n') && !currentDesc.endsWith(' ') ? '\n' : '';
                      setManualDescription(currentDesc + separator + phrase);
                    }}
                  />
                </div>
                <textarea
                  value={manualDescription}
                  onChange={(e) => setManualDescription(e.target.value)}
                  placeholder="Add details about this item..."
                  rows={3}
                  className="input"
                  spellCheck={true}
                  autoCorrect="on"
                  autoCapitalize="sentences"
                  autoComplete="one-time-code"
                />
              </div>

              {/* Location input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Location (optional):
                </label>

                {/* Location dropdown */}
                <select
                  value={locationMode === 'new' ? '__new__' : (selectedLocationId || '')}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '__new__') {
                      handleCreateNewLocation();
                    } else if (value === '') {
                      // No location selected
                      setSelectedLocationId(null);
                      setManualLocation('');
                      setManualLocationImage(null);
                      setLocationMode('select');
                    } else {
                      handleSelectSavedLocation(value);
                    }
                  }}
                  className="input"
                >
                  <option value="">No location</option>
                  {savedLocations.map((loc) => (
                    <option key={loc.id} value={loc.id}>
                      {loc.name}
                    </option>
                  ))}
                  <option value="__new__">+ Add new location...</option>
                </select>

                {/* Show selected location preview with image */}
                {locationMode === 'select' && selectedLocationId && (() => {
                  const selectedLocation = savedLocations.find(loc => loc.id === selectedLocationId);
                  return (
                    <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        {selectedLocation?.imageUrl ? (
                          <img
                            src={selectedLocation.imageUrl}
                            alt="Location"
                            className="w-12 h-12 rounded-lg object-cover"
                          />
                        ) : (
                          <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center">
                            <MapPin className="w-6 h-6 text-gray-400" />
                          </div>
                        )}
                        <div>
                          <p className="text-sm font-medium text-gray-900">{manualLocation}</p>
                          <p className="text-xs text-gray-500">Saved location</p>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* New location input - show when adding new location */}
                {locationMode === 'new' && (
                  <div className="mt-3 space-y-3">
                    <input
                      type="text"
                      value={manualLocation}
                      onChange={(e) => setManualLocation(e.target.value)}
                      placeholder="e.g. Top drawer of pantry, Under the bed"
                      className="input"
                      spellCheck={true}
                      autoCorrect="on"
                      autoCapitalize="sentences"
                  autoComplete="one-time-code"
                    />

                    {/* Location image */}
                    <input
                      id="location-image-input"
                      ref={locationImageInputRef}
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={handleLocationImageSelect}
                      className="sr-only"
                    />
                    {manualLocationImage ? (
                      <div className="flex items-center gap-3">
                        <div className="relative w-16 h-16">
                          <img
                            src={manualLocationImage.preview}
                            alt="Location"
                            className="w-full h-full object-cover rounded-lg"
                          />
                          <button
                            onClick={() => setManualLocationImage(null)}
                            className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center shadow-md"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                        <span className="text-sm text-gray-600">Location photo added</span>
                      </div>
                    ) : (
                      <label
                        htmlFor="location-image-input"
                        className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1 cursor-pointer"
                      >
                        <Camera className="w-4 h-4" />
                        Add photo of location
                      </label>
                    )}
                  </div>
                )}
              </div>

              {/* Condition dropdown */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Condition:
                </label>
                <select
                  value={manualCondition}
                  onChange={(e) => setManualCondition(e.target.value as ItemCondition)}
                  className="input"
                >
                  {ITEM_CONDITIONS.map((cond) => (
                    <option key={cond} value={cond}>
                      {ITEM_CONDITION_LABELS[cond]}
                    </option>
                  ))}
                </select>
              </div>

              {displayError && (
                <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>{displayError}</span>
                </div>
              )}

              {/* Image preview grid */}
              {manualImages.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-sm font-medium text-gray-700">
                    Photos ({manualImages.length}) - First photo is thumbnail
                  </h3>
                  <div className="grid grid-cols-3 gap-2">
                    {manualImages.map((img, index) => (
                      <div key={index} className="relative aspect-square">
                        <img
                          src={img.preview}
                          alt={`Photo ${index + 1}`}
                          className={clsx(
                            'w-full h-full object-cover rounded-lg',
                            index === 0 && 'ring-2 ring-primary-500'
                          )}
                        />
                        {index === 0 && (
                          <span className="absolute top-1 left-1 bg-primary-500 text-white text-xs px-1.5 py-0.5 rounded">
                            Thumbnail
                          </span>
                        )}
                        {/* Rotation buttons */}
                        <div className="absolute bottom-1 left-1 right-1 flex justify-center gap-1">
                          <button
                            onClick={() => rotateManualImage(index, 'left')}
                            className="w-7 h-7 bg-black/60 hover:bg-black/80 text-white rounded-full flex items-center justify-center transition-colors"
                            title="Rotate left"
                          >
                            <RotateCcw className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => rotateManualImage(index, 'right')}
                            className="w-7 h-7 bg-black/60 hover:bg-black/80 text-white rounded-full flex items-center justify-center transition-colors"
                            title="Rotate right"
                          >
                            <RotateCw className="w-4 h-4" />
                          </button>
                        </div>
                        <button
                          onClick={() => removeManualImage(index)}
                          className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center shadow-md"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Hidden file inputs */}
              <input
                id="manual-file-input"
                ref={manualFileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleManualFileSelect}
                className="sr-only"
              />
              <input
                id="manual-camera-input"
                ref={manualCameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleManualFileSelect}
                className="sr-only"
              />

              {/* Add photo buttons - using labels for mobile compatibility */}
              <div className="flex gap-3">
                <label
                  htmlFor="manual-camera-input"
                  className="btn-secondary flex-1 flex items-center justify-center cursor-pointer"
                >
                  <Camera className="w-5 h-5 mr-2" />
                  Take Photo
                </label>
                <label
                  htmlFor="manual-file-input"
                  className="btn-secondary flex-1 flex items-center justify-center cursor-pointer"
                >
                  <ImagePlus className="w-5 h-5 mr-2" />
                  Choose Photos
                </label>
              </div>

              {/* Create button */}
              <button
                ref={addItemButtonRef}
                onClick={handleCreateManualItem}
                disabled={!selectedInventory || manualImages.length === 0}
                className="btn-primary w-full py-4 text-lg"
              >
                <Package className="w-6 h-6 mr-2" />
                Add Item
              </button>

              <p className="text-sm text-gray-500 text-center">
                Tip: Take clear photos from multiple angles. The first photo will be the thumbnail.
              </p>
            </div>
          )}

          {manualSessionState === 'saving' && (
            <div className="card p-8 text-center space-y-4">
              <div className="relative">
                <Loader2 className="w-16 h-16 mx-auto text-primary-600 animate-spin" />
              </div>
              <p className="text-lg font-medium text-gray-900">
                Saving item...
              </p>
              <p className="text-gray-600">
                Uploading {manualImages.length} photo{manualImages.length > 1 ? 's' : ''}.
              </p>
            </div>
          )}

          {manualSessionState === 'done' && manualCreatedItem && (
            <div className="card p-6 space-y-6">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center">
                  <Check className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="mt-4 text-xl font-medium text-gray-900">
                  Item Added!
                </h3>
                <p className="mt-2 text-gray-600">
                  "{manualCreatedItem.title}" with {manualImages.length} photo{manualImages.length > 1 ? 's' : ''} has been added to your inventory.
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleNewManualItem}
                  className="btn-secondary flex-1"
                >
                  <Edit3 className="w-4 h-4 mr-2" />
                  Add Another
                </button>
                <button
                  onClick={finishAndReview}
                  className="btn-primary flex-1"
                >
                  <Package className="w-4 h-4 mr-2" />
                  View Inventory
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* ==================== VOICE MODE ==================== */}
      {inputMode === 'voice' && (
        <>
          {(sessionState === 'idle' || sessionState === 'error') && (
            <div className="card p-6 space-y-6">
              {/* Inventory selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Add items to:
                </label>
                <select
                  value={selectedInventory}
                  onChange={(e) => setSelectedInventory(e.target.value)}
                  className="input"
                >
                  <option value="">Select an inventory</option>
                  {inventories?.map((inv) => (
                    <option key={inv.id} value={inv.id}>
                      {inv.name} ({inv.itemCount} items)
                    </option>
                  ))}
                </select>
              </div>

              {displayError && (
                <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>{displayError}</span>
                </div>
              )}

              {/* Start button */}
              <button
                onClick={handleStartSession}
                disabled={!selectedInventory}
                className="btn-primary w-full py-4 text-lg"
              >
                <Mic className="w-6 h-6 mr-2" />
                Start Recording
              </button>

              <p className="text-sm text-gray-500 text-center">
                Tip: Walk room by room and describe what you see. Mention brands, colors, and sizes when you remember them.
              </p>
            </div>
          )}

          {sessionState === 'connecting' && (
            <div className="card p-8 text-center">
              <div className="animate-pulse">
                <Mic className="w-16 h-16 mx-auto text-primary-600" />
              </div>
              <p className="mt-4 text-gray-600">Connecting to AI assistant...</p>
              <p className="mt-2 text-sm text-gray-500">
                Please allow microphone access when prompted
              </p>
            </div>
          )}

          {sessionState === 'active' && (
            <div className="space-y-6">
              {/* Recording indicator with audio level */}
              <div className="card p-8 text-center">
                <div className="relative">
                  <div
                    className={clsx(
                      'absolute inset-0 mx-auto w-24 h-24 rounded-full bg-red-500 transition-transform',
                      audioLevel > 0.1 && 'animate-ping opacity-20'
                    )}
                    style={{
                      transform: `scale(${1 + audioLevel * 0.5})`,
                    }}
                  />
                  <div className="relative w-24 h-24 mx-auto bg-red-500 rounded-full flex items-center justify-center">
                    <Mic className="w-12 h-12 text-white" />
                  </div>
                </div>
                <p className="mt-4 text-lg font-medium text-gray-900">
                  Listening...
                </p>
                <p className="mt-2 text-gray-600">
                  Describe your items naturally. The AI is cataloging as you speak.
                </p>
              </div>

              {/* Extracted items */}
              {extractedItems.length > 0 && (
                <div className="card p-4">
                  <h3 className="font-medium text-gray-900 mb-3">
                    Items Found ({extractedItems.length})
                  </h3>
                  <ul className="space-y-2">
                    {extractedItems.map((item) => (
                      <li
                        key={item.id}
                        className="flex items-center gap-3 p-2 bg-green-50 rounded-lg"
                      >
                        <Check className="w-4 h-4 text-green-600" />
                        <span className="text-gray-900">{item.title}</span>
                        <span className="text-xs text-gray-500">{item.category}</span>
                        {item.confidence < 0.7 && (
                          <span className="text-xs text-amber-600 ml-auto">
                            Needs review
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Stop button */}
              <button
                onClick={endSession}
                className="btn-danger w-full py-4 text-lg"
              >
                <MicOff className="w-6 h-6 mr-2" />
                Stop Recording
              </button>
            </div>
          )}

          {sessionState === 'ending' && (
            <div className="card p-8 text-center">
              <div className="animate-spin w-8 h-8 mx-auto border-4 border-primary-200 border-t-primary-600 rounded-full" />
              <p className="mt-4 text-gray-600">Finishing session...</p>
            </div>
          )}

          {sessionState === 'ended' && (
            <div className="card p-6 space-y-6">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center">
                  <Check className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="mt-4 text-xl font-medium text-gray-900">
                  Session Complete!
                </h3>
                <p className="mt-2 text-gray-600">
                  {extractedItems.length > 0
                    ? `Found ${extractedItems.length} items`
                    : 'No items were captured. Try speaking more clearly about your belongings.'}
                </p>
              </div>

              {extractedItems.length > 0 && (
                <div className="border rounded-lg p-4 bg-gray-50">
                  <ul className="space-y-1 text-sm">
                    {extractedItems.map((item) => (
                      <li key={item.id} className="flex items-center gap-2">
                        <Check className="w-3 h-3 text-green-600" />
                        <span>{item.title}</span>
                        <span className="text-gray-400">({item.category})</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={handleNewSession}
                  className="btn-secondary flex-1"
                >
                  <Mic className="w-4 h-4 mr-2" />
                  New Session
                </button>
                <button
                  onClick={finishAndReview}
                  className="btn-primary flex-1"
                >
                  <Package className="w-4 h-4 mr-2" />
                  Review Items
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* ==================== IMAGE MODE ==================== */}
      {inputMode === 'image' && (
        <>
          {imageSessionState === 'idle' && (
            <div className="card p-6 space-y-6">
              {/* Inventory selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Add items to:
                </label>
                <select
                  value={selectedInventory}
                  onChange={(e) => setSelectedInventory(e.target.value)}
                  className="input"
                >
                  <option value="">Select an inventory</option>
                  {inventories?.map((inv) => (
                    <option key={inv.id} value={inv.id}>
                      {inv.name} ({inv.itemCount} items)
                    </option>
                  ))}
                </select>
              </div>

              {displayError && (
                <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>{displayError}</span>
                </div>
              )}

              {/* Image preview grid */}
              {capturedImages.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-sm font-medium text-gray-700">
                    Photos ({capturedImages.length})
                  </h3>
                  <div className="grid grid-cols-3 gap-2">
                    {capturedImages.map((img, index) => (
                      <div key={index} className="relative aspect-square">
                        <img
                          src={img.preview}
                          alt={`Capture ${index + 1}`}
                          className="w-full h-full object-cover rounded-lg"
                        />
                        <button
                          onClick={() => removeImage(index)}
                          className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center shadow-md"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Hidden file inputs */}
              <input
                id="image-file-input"
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileSelect}
                className="sr-only"
              />
              <input
                id="image-camera-input"
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleFileSelect}
                className="sr-only"
              />

              {/* Add photo buttons - using labels for mobile compatibility */}
              <div className="flex gap-3">
                <label
                  htmlFor="image-camera-input"
                  className="btn-secondary flex-1 flex items-center justify-center cursor-pointer"
                >
                  <Camera className="w-5 h-5 mr-2" />
                  Take Photo
                </label>
                <label
                  htmlFor="image-file-input"
                  className="btn-secondary flex-1 flex items-center justify-center cursor-pointer"
                >
                  <ImagePlus className="w-5 h-5 mr-2" />
                  Choose Photos
                </label>
              </div>

              {/* Analyze button */}
              <button
                onClick={handleAnalyzeImages}
                disabled={!selectedInventory || capturedImages.length === 0}
                className="btn-primary w-full py-4 text-lg"
              >
                <Camera className="w-6 h-6 mr-2" />
                Analyze {capturedImages.length > 0 ? `${capturedImages.length} Photo${capturedImages.length > 1 ? 's' : ''}` : 'Photos'}
              </button>

              <p className="text-sm text-gray-500 text-center">
                Tip: Take clear photos of shelves, drawers, or rooms. The AI will identify all visible items.
              </p>
            </div>
          )}

          {imageSessionState === 'analyzing' && (
            <div className="card p-8 text-center space-y-4">
              <div className="relative">
                <Loader2 className="w-16 h-16 mx-auto text-primary-600 animate-spin" />
              </div>
              <p className="text-lg font-medium text-gray-900">
                Analyzing {capturedImages.length} photo{capturedImages.length > 1 ? 's' : ''}...
              </p>
              <p className="text-gray-600">
                AI is identifying all items in your images. This may take a moment.
              </p>
              {/* Show thumbnails being analyzed */}
              <div className="flex justify-center gap-2 mt-4">
                {capturedImages.slice(0, 4).map((img, index) => (
                  <div key={index} className="w-12 h-12 rounded-lg overflow-hidden opacity-50">
                    <img src={img.preview} alt="" className="w-full h-full object-cover" />
                  </div>
                ))}
                {capturedImages.length > 4 && (
                  <div className="w-12 h-12 rounded-lg bg-gray-200 flex items-center justify-center text-xs text-gray-500">
                    +{capturedImages.length - 4}
                  </div>
                )}
              </div>
            </div>
          )}

          {imageSessionState === 'done' && (
            <div className="card p-6 space-y-6">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center">
                  <Check className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="mt-4 text-xl font-medium text-gray-900">
                  Analysis Complete!
                </h3>
                <p className="mt-2 text-gray-600">
                  {imageItems.length > 0
                    ? `Found ${imageItems.length} items in ${capturedImages.length} photo${capturedImages.length > 1 ? 's' : ''}`
                    : 'No items were identified. Try taking clearer photos with better lighting.'}
                </p>
              </div>

              {imageItems.length > 0 && (
                <div className="border rounded-lg p-4 bg-gray-50 max-h-64 overflow-y-auto">
                  <ul className="space-y-1 text-sm">
                    {imageItems.map((item) => (
                      <li key={item.id} className="flex items-center gap-2">
                        <Check className="w-3 h-3 text-green-600 flex-shrink-0" />
                        <span className="truncate">{item.title}</span>
                        <span className="text-gray-400 flex-shrink-0">({item.category})</span>
                        {item.confidence < 0.7 && (
                          <span className="text-xs text-amber-600 ml-auto flex-shrink-0">
                            Review
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={handleNewImageSession}
                  className="btn-secondary flex-1"
                >
                  <Camera className="w-4 h-4 mr-2" />
                  New Photos
                </button>
                <button
                  onClick={finishAndReview}
                  className="btn-primary flex-1"
                >
                  <Package className="w-4 h-4 mr-2" />
                  Review Items
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* AI Credits Paywall Modal */}
      <PaywallModal
        isOpen={showPaywall}
        onClose={() => setShowPaywall(false)}
        featureName={paywallFeature}
      />
    </div>
  );
}
